<template>
  <div class="formula-builder">
    <div class="caption grey--text text--darken-1 mb-2">
      Variables disponibles
      <span v-if="!readonly" class="grey--text">— click para insertar en la fórmula</span>
    </div>
    <div class="d-flex flex-wrap mb-3" style="gap: 8px;">
      <v-chip
        v-for="variable in variables"
        :key="variable.name"
        small
        :color="variable.color"
        dark
        :class="['var-chip font-weight-medium', { 'var-chip--readonly': readonly }]"
        @click="!readonly && insertVariable(variable.name)"
      >
        <v-icon left x-small>{{ variable.icon }}</v-icon>
        {{ variable.name }}
      </v-chip>
    </div>

    <div v-if="!readonly" class="d-flex align-center flex-wrap mb-3" style="gap: 4px;">
      <span class="caption grey--text mr-1">Plantillas:</span>
      <v-chip
        v-for="preset in presets"
        :key="preset.label"
        x-small
        outlined
        color="blue-grey"
        class="preset-chip"
        @click="setPreset(preset.formula)"
      >
        {{ preset.label }}
      </v-chip>
    </div>

    <div
      class="formula-editor-wrapper"
      :class="{
        'formula-editor-wrapper--error': localFormula && !formulaValida,
        'formula-editor-wrapper--readonly': readonly,
      }"
    >
      <v-textarea
        ref="formulaInput"
        v-model="localFormula"
        :readonly="readonly"
        auto-grow
        rows="2"
        dense
        hide-details
        flat
        solo
        class="formula-textarea"
        placeholder="ej: precio_compra * (1 - descuento/100) * (1 + iva/100) * (1 + margen/100)"
        @input="onFormulaInput"
      />
      <div
        v-if="localFormula"
        class="formula-status caption"
        :class="formulaValida ? 'formula-status--ok' : 'formula-status--err'"
      >
        <v-icon x-small class="mr-1">{{ formulaValida ? 'check_circle' : 'cancel' }}</v-icon>
        {{ formulaValida ? 'Fórmula válida' : 'Error en la fórmula' }}
      </div>
    </div>

    <div class="d-flex align-start flex-wrap mt-5" style="gap: 16px;">
      <v-select
        v-model="localConfig.tipoRedondeo"
        :items="tipoRedondeoOptions"
        :disabled="readonly"
        label="Redondeo"
        outlined
        dense
        persistent-hint
        hint="Si queda sin override, usa el redondeo del producto."
        style="max-width: 230px;"
        @change="emit"
      />
      <v-text-field
        v-if="!readonly"
        v-model.number="localConfig.margenDefault"
        label="Definir margen para toda la lista (%)"
        hint="Opcional. Si no se define, usa el margen individual del producto."
        persistent-hint
        type="number"
        min="0"
        max="1000"
        step="0.5"
        dense
        outlined
        clearable
        style="width: 300px;"
        @input="emit"
        @click:clear="clearMargenDefault"
      />
      <div v-else-if="localConfig.margenDefault != null" class="caption grey--text">
        Margen default: <strong>{{ localConfig.margenDefault }}%</strong>
      </div>
    </div>

    <v-card outlined class="mt-4 preview-card" color="grey lighten-5">
      <v-card-text class="pa-3">
        <div class="caption grey--text text--darken-1 mb-3">Ejemplo</div>
        <div class="d-flex align-center flex-wrap" style="gap: 12px;">
          <v-text-field
            v-model.number="preview.precio_compra"
            label="Precio compra"
            type="number"
            dense
            outlined
            hide-details
            style="max-width: 130px;"
            prefix="$"
            background-color="white"
          />
          <v-text-field
            v-model.number="preview.descuento"
            label="Descuento"
            type="number"
            min="0"
            max="100"
            dense
            outlined
            hide-details
            style="max-width: 115px;"
            suffix="%"
            background-color="white"
          />
          <v-text-field
            v-model.number="preview.iva"
            label="IVA"
            type="number"
            dense
            outlined
            hide-details
            style="max-width: 100px;"
            suffix="%"
            background-color="white"
          />
          <v-text-field
            v-model.number="preview.margen"
            label="Margen"
            type="number"
            dense
            outlined
            hide-details
            style="max-width: 105px;"
            suffix="%"
            background-color="white"
          />
          <div class="d-flex align-center" style="gap: 10px;">
            <v-icon color="grey">arrow_forward</v-icon>
            <div
              class="preview-result"
              :class="precioCalculado !== null ? 'preview-result--ok' : 'preview-result--err'"
            >
              <span v-if="precioCalculado !== null">${{ precioCalculado.toFixed(2) }}</span>
              <span v-else class="caption">—</span>
            </div>
          </div>
        </div>
        <div v-if="formulaEvalSteps" class="caption grey--text mt-2 formula-steps">
          {{ formulaEvalSteps }}
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
const DEFAULT_FORMULA =
  "precio_compra * (1 - descuento/100) * (1 + iva/100) * (1 + margen/100)";

function evalFormula(formula, vars) {
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function(
      "precio_compra",
      "descuento",
      "iva",
      "margen",
      `"use strict"; return (${formula});`
    );
    const result = fn(vars.precio_compra, vars.descuento, vars.iva, vars.margen);
    return typeof result === "number" && isFinite(result) && result >= 0 ? result : null;
  } catch {
    return null;
  }
}

function applyRounding(precio, tipoRedondeo) {
  switch (tipoRedondeo) {
    case "centavos":
      return Math.ceil(precio);
    case "multiplo_10":
      return Math.ceil(precio / 10) * 10;
    case "multiplo_100":
      return Math.ceil(precio / 100) * 100;
    default:
      return precio;
  }
}

export default {
  name: "FormulaBuilder",
  props: {
    value: {
      type: Object,
      default: () => ({
        formula: DEFAULT_FORMULA,
        margenDefault: null,
        tipoRedondeo: null,
      }),
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const cfg = this.value || {};
    return {
      localFormula: cfg.formula || DEFAULT_FORMULA,
      localConfig: {
        tipoRedondeo: cfg.tipoRedondeo ?? null,
        margenDefault: cfg.margenDefault ?? null,
      },
      preview: {
        precio_compra: 100,
        descuento: 10,
        iva: 21,
        margen: 30,
      },
      variables: [
        { name: "precio_compra", icon: "attach_money", color: "teal darken-1" },
        { name: "descuento", icon: "local_offer", color: "orange darken-1" },
        { name: "iva", icon: "account_balance", color: "blue darken-1" },
        { name: "margen", icon: "trending_up", color: "purple darken-1" },
      ],
      presets: [
        { label: "Estándar", formula: DEFAULT_FORMULA },
        { label: "Sin IVA", formula: "precio_compra * (1 - descuento/100) * (1 + margen/100)" },
        { label: "Sin descuento", formula: "precio_compra * (1 + iva/100) * (1 + margen/100)" },
        { label: "Solo margen", formula: "precio_compra * (1 + margen/100)" },
      ],
      tipoRedondeoOptions: [
        { text: "Sin override (usar producto)", value: null },
        { text: "Sin redondeo", value: "ninguno" },
        { text: "Entero superior", value: "centavos" },
        { text: "Múltiplo de 10", value: "multiplo_10" },
        { text: "Múltiplo de 100", value: "multiplo_100" },
      ],
    };
  },
  computed: {
    formulaValida() {
      if (!this.localFormula) return false;
      return evalFormula(this.localFormula, {
        precio_compra: 100,
        descuento: 10,
        iva: 21,
        margen: 30,
      }) !== null;
    },
    precioCalculado() {
      const { precio_compra, descuento, iva, margen } = this.preview;
      if (!precio_compra || precio_compra <= 0 || !this.localFormula) return null;
      const raw = evalFormula(this.localFormula, { precio_compra, descuento, iva, margen });
      if (raw == null) return null;
      const rounded = applyRounding(raw, this.localConfig.tipoRedondeo);
      return parseFloat(rounded.toFixed(2));
    },
    formulaEvalSteps() {
      const { precio_compra, descuento, iva, margen } = this.preview;
      if (!precio_compra || !this.localFormula || this.precioCalculado == null) return null;
      return this.localFormula
        .replace(/precio_compra/g, precio_compra)
        .replace(/descuento/g, descuento)
        .replace(/iva/g, iva)
        .replace(/margen/g, margen);
    },
  },
  watch: {
    value(val) {
      if (!val) return;
      this.localFormula = val.formula || DEFAULT_FORMULA;
      this.localConfig.tipoRedondeo = val.tipoRedondeo ?? null;
      this.localConfig.margenDefault = val.margenDefault ?? null;
    },
  },
  methods: {
    insertVariable(varName) {
      const ta = this.$refs.formulaInput && this.$refs.formulaInput.$refs.input;
      if (!ta) {
        this.localFormula = (this.localFormula || "") + varName;
        this.emit();
        return;
      }
      const start = ta.selectionStart ?? (this.localFormula || "").length;
      const end = ta.selectionEnd ?? start;
      const current = this.localFormula || "";
      this.localFormula = current.substring(0, start) + varName + current.substring(end);
      this.$nextTick(() => {
        const pos = start + varName.length;
        ta.setSelectionRange(pos, pos);
        ta.focus();
      });
      this.emit();
    },
    setPreset(formula) {
      this.localFormula = formula;
      this.emit();
    },
    onFormulaInput() {
      this.emit();
    },
    clearMargenDefault() {
      this.localConfig.margenDefault = null;
      this.emit();
    },
    emit() {
      this.$emit("input", {
        formula: this.localFormula,
        margenDefault: this.localConfig.margenDefault,
        tipoRedondeo: this.localConfig.tipoRedondeo,
      });
    },
  },
};
</script>

<style scoped>
.var-chip {
  cursor: pointer;
  transition: transform 0.1s, opacity 0.15s;
  font-family: "Roboto Mono", monospace, monospace;
  letter-spacing: 0;
}
.var-chip:hover:not(.var-chip--readonly) {
  transform: translateY(-1px);
}
.var-chip--readonly {
  cursor: default;
}
.preset-chip {
  cursor: pointer;
  font-size: 11px !important;
}
.preset-chip:hover {
  opacity: 0.8;
}
.formula-editor-wrapper {
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background: #1e1e2e;
  overflow: hidden;
  transition: border-color 0.2s;
  position: relative;
}
.formula-editor-wrapper--error {
  border-color: #ef5350 !important;
}
.formula-editor-wrapper--readonly {
  background: #263238;
  border-color: #37474f;
}
.formula-textarea >>> textarea {
  font-family: "Roboto Mono", "Courier New", monospace !important;
  font-size: 13px !important;
  line-height: 1.6 !important;
  color: #a6e22e !important;
  caret-color: #fff !important;
  padding: 14px 12px !important;
  min-height: 72px !important;
}
.formula-textarea >>> .v-input__slot {
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
}
.formula-textarea >>> .v-text-field__slot::placeholder {
  color: #546e7a !important;
}
.formula-status {
  padding: 2px 10px 6px;
  display: flex;
  align-items: center;
}
.formula-status--ok {
  color: #66bb6a;
}
.formula-status--err {
  color: #ef9a9a;
}
.preview-card {
  border-radius: 8px !important;
}
.preview-result {
  min-width: 96px;
  height: 40px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.15rem;
  padding: 0 12px;
}
.preview-result--ok {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1.5px solid #a5d6a7;
}
.preview-result--err {
  background: #fafafa;
  color: #bdbdbd;
  border: 1.5px solid #e0e0e0;
}
.formula-steps {
  font-family: "Roboto Mono", monospace;
  font-size: 11px;
  word-break: break-all;
  color: #78909c;
  line-height: 1.5;
}
</style>
